/**
 * OmegaNewsCard (iter 134) — rich news card with image, source chip,
 * relative timestamp, sentiment badge, Omega AI insight, cross-source
 * confirmation, and on-demand earnings scenarios.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SENT_COLOR = {
  bullish: { bg: "rgba(74,222,128,0.14)", border: "rgba(74,222,128,0.40)", text: "#4ade80", emoji: "📈" },
  bearish: { bg: "rgba(248,113,113,0.14)", border: "rgba(248,113,113,0.40)", text: "#f87171", emoji: "📉" },
  neutral: { bg: "rgba(148,163,184,0.14)", border: "rgba(148,163,184,0.32)", text: "#94a3b8", emoji: "➖" },
};
const CRED_COLOR = {
  high: { text: "#4ade80", label: "Verified" },
  medium: { text: "#fbbf24", label: "Moderate" },
  low: { text: "#f87171", label: "⚠ Unverified" },
};

export default function OmegaNewsCard({ article, accent = "#a78bfa" }) {
  const [scenariosOpen, setScenariosOpen] = useState(false);
  const [scenarios, setScenarios] = useState(null);
  const [deepDive, setDeepDive] = useState(null);
  const [deepOpen, setDeepOpen] = useState(false);
  const [tradeIdea, setTradeIdea] = useState(null);
  const [tradeOpen, setTradeOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // iter 141 — popup summary modal (replaces direct nav to Yahoo on title tap)
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const sent = SENT_COLOR[article.sentiment || "neutral"] || SENT_COLOR.neutral;
  const cred = CRED_COLOR[article.credibility || "medium"] || CRED_COLOR.medium;

  const loadScenarios = async () => {
    if (scenariosOpen) { setScenariosOpen(false); return; }
    setScenariosOpen(true);
    if (scenarios) return;
    setBusy(true);
    try {
      const r = await axios.get(`${API}/market/news/scenarios/${article.ticker}`);
      setScenarios(r.data);
    } catch (e) {
      setScenarios({ error: e?.message || "load failed" });
    }
    setBusy(false);
  };

  const loadDeepDive = async () => {
    if (deepOpen) { setDeepOpen(false); return; }
    setDeepOpen(true);
    if (deepDive) return;
    setBusy(true);
    try {
      const r = await axios.get(`${API}/market/news/deep-dive/${article.id}`);
      setDeepDive(r.data);
    } catch (e) {
      setDeepDive({ error: e?.response?.data?.detail || e?.message || "load failed" });
    }
    setBusy(false);
  };

  const loadTradeIdea = async () => {
    if (tradeOpen) { setTradeOpen(false); return; }
    setTradeOpen(true);
    if (tradeIdea) return;
    setBusy(true);
    try {
      const r = await axios.get(`${API}/market/news/trade-idea/${article.id}`);
      setTradeIdea(r.data);
    } catch (e) {
      setTradeIdea({ error: e?.response?.data?.detail || e?.message || "load failed" });
    }
    setBusy(false);
  };

  // iter 141 — open the Omega summary popup. Loads the deep-dive on first open
  // and caches; the popup never navigates externally — only a "Read full article"
  // button inside it does that.
  const openSummaryModal = async () => {
    setSummaryOpen(true);
    if (summaryData) return;
    setSummaryLoading(true);
    try {
      const r = await axios.get(`${API}/market/news/deep-dive/${article.id}`);
      setSummaryData(r.data);
    } catch (e) {
      setSummaryData({ error: e?.response?.data?.detail || e?.message || "load failed" });
    } finally {
      setSummaryLoading(false);
    }
  };

  // Close modal on ESC
  useEffect(() => {
    if (!summaryOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setSummaryOpen(false); };
    window.addEventListener("keydown", onKey);
    // Prevent body scroll while modal is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [summaryOpen]);

  const sources = article.confirmed_by && article.confirmed_by.length > 0 ? article.confirmed_by : [article.source];

  return (
    <article
      data-testid={`news-card-${article.id}`}
      style={{
        background: "var(--layer1, #14141f)",
        border: `1px solid rgba(255,255,255,0.06)`,
        borderRadius: 14,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Image */}
      {article.image && (
        <div style={{ width: "100%", height: 140, overflow: "hidden", background: "rgba(0,0,0,0.4)", position: "relative" }}>
          <img
            src={article.image}
            alt=""
            onError={e => { e.currentTarget.style.display = "none"; }}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
          {/* Sentiment ribbon overlay */}
          <span style={{
            position: "absolute", top: 8, right: 8,
            padding: "3px 9px", borderRadius: 999,
            background: sent.bg, border: `1px solid ${sent.border}`, color: sent.text,
            fontSize: 10, fontWeight: 800, letterSpacing: 0.4,
            backdropFilter: "blur(6px)",
          }}>{sent.emoji} {(article.sentiment || "neutral").toUpperCase()}</span>
        </div>
      )}

      <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
        {/* Meta row: source + time + cred */}
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, fontSize: 10 }}>
          {sources.slice(0, 3).map((s, i) => (
            <span key={i} style={{
              padding: "2px 7px", borderRadius: 999,
              background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)",
              color: "var(--muted, #aaa)", fontWeight: 700, letterSpacing: 0.3,
            }}>{s}</span>
          ))}
          {sources.length > 3 && <span style={{ color: "var(--muted, #aaa)", fontWeight: 700 }}>+{sources.length - 3}</span>}
          <span style={{ color: "var(--muted, #aaa)", marginLeft: 2 }}>·</span>
          <span style={{ color: "var(--muted, #aaa)", fontWeight: 700 }}>{article.publishedAgo || "—"}</span>
          <span style={{ color: "var(--muted, #aaa)" }}>·</span>
          <span style={{ color: cred.text, fontWeight: 800, letterSpacing: 0.3 }}>{cred.label}</span>
          {!article.image && (
            <span style={{ marginLeft: "auto", padding: "2px 7px", borderRadius: 999, background: sent.bg, color: sent.text, border: `1px solid ${sent.border}`, fontWeight: 800, fontSize: 9 }}>
              {sent.emoji} {(article.sentiment || "neutral").toUpperCase()}
            </span>
          )}
        </div>

        {/* Headline — iter 141: opens Omega summary modal instead of nav'ing to Yahoo */}
        <button
          type="button"
          onClick={openSummaryModal}
          data-testid={`news-card-headline-${article.id}`}
          style={{
            color: "var(--text, #e6e6f5)", fontSize: 15, fontWeight: 700,
            textDecoration: "none", lineHeight: 1.35,
            background: "none", border: "none", padding: 0, textAlign: "left",
            cursor: "pointer", fontFamily: "inherit",
          }}
        >{article.title}</button>

        {/* Omega's take */}
        {article.omega_take && (
          <div data-testid={`news-card-omega-${article.id}`} style={{
            padding: "9px 11px", borderRadius: 10,
            background: `${accent}10`, border: `1px solid ${accent}22`,
            fontSize: 12, lineHeight: 1.5, color: "var(--text, #e6e6f5)",
          }}>
            <span style={{ color: accent, fontWeight: 800, fontSize: 9, letterSpacing: 1.3, display: "block", marginBottom: 3 }}>
              OMEGA'S TAKE
            </span>
            {article.omega_take}
          </div>
        )}

        {/* Footer actions */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2, flexWrap: "wrap" }}>
          {article.impact_score != null && (
            <span style={{ fontSize: 10, color: "var(--muted, #aaa)" }}>
              Impact <strong style={{ color: "var(--text, #e6e6f5)" }}>{article.impact_score}</strong>/100
            </span>
          )}
          {article.category && article.category !== "general" && (
            <span style={{
              fontSize: 9, padding: "2px 7px", borderRadius: 999,
              background: "rgba(96,165,250,0.10)", color: "#60a5fa",
              border: "1px solid rgba(96,165,250,0.25)", fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase",
            }}>{article.category}</span>
          )}
          {/* iter 135 — Deep dive + Trade idea + Scenarios buttons */}
          <button
            data-testid={`news-card-deep-${article.id}`}
            onClick={loadDeepDive}
            style={{
              padding: "5px 10px", borderRadius: 999,
              background: deepOpen ? `${accent}28` : "rgba(167,139,250,0.10)",
              border: `1px solid ${accent}40`, color: accent,
              fontSize: 10, fontWeight: 800, cursor: "pointer", letterSpacing: 0.4,
            }}
          >{deepOpen ? "▾ Hide deep dive" : "🔍 Deep dive"}</button>
          <button
            data-testid={`news-card-idea-${article.id}`}
            onClick={loadTradeIdea}
            style={{
              padding: "5px 10px", borderRadius: 999,
              background: tradeOpen ? "rgba(74,222,128,0.22)" : "rgba(74,222,128,0.10)",
              border: "1px solid rgba(74,222,128,0.35)", color: "#4ade80",
              fontSize: 10, fontWeight: 800, cursor: "pointer", letterSpacing: 0.4,
            }}
          >{tradeOpen ? "▾ Hide idea" : "💡 Trade idea"}</button>
          {article.is_earnings && (
            <button
              data-testid={`news-card-scenarios-${article.id}`}
              onClick={loadScenarios}
              style={{
                padding: "5px 10px", borderRadius: 999,
                background: scenariosOpen ? `${accent}28` : "rgba(167,139,250,0.10)",
                border: `1px solid ${accent}40`, color: accent,
                fontSize: 10, fontWeight: 800, cursor: "pointer", letterSpacing: 0.4,
              }}
            >{scenariosOpen ? "▾ Hide" : "📊 Scenarios"}</button>
          )}
          <a
            href={article.link} target="_blank" rel="noopener noreferrer"
            data-testid={`news-card-source-${article.id}`}
            style={{
              marginLeft: "auto", padding: "5px 11px", borderRadius: 999,
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)",
              color: "var(--muted, #aaa)", fontSize: 10, fontWeight: 700,
              textDecoration: "none", letterSpacing: 0.3,
            }}
          >Read →</a>
        </div>

        {/* Deep dive panel */}
        {deepOpen && (
          <div data-testid={`news-card-deep-body-${article.id}`} style={{
            marginTop: 4, padding: 12, borderRadius: 10,
            background: "rgba(0,0,0,0.3)", border: `1px solid ${accent}22`,
            whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.55, color: "var(--text, #e6e6f5)",
          }}>
            {busy && !deepDive && <span style={{ color: "var(--muted, #aaa)" }}>Omega is thinking…</span>}
            {deepDive?.breakdown && deepDive.breakdown}
            {deepDive?.error && <span style={{ color: "#f87171" }}>{deepDive.error}</span>}
          </div>
        )}

        {/* Trade idea panel */}
        {tradeOpen && (
          <div data-testid={`news-card-idea-body-${article.id}`} style={{
            marginTop: 4, padding: 12, borderRadius: 10,
            background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.25)",
            fontSize: 12, lineHeight: 1.55, color: "var(--text, #e6e6f5)",
          }}>
            {busy && !tradeIdea && <span style={{ color: "var(--muted, #aaa)" }}>Building defined-risk play…</span>}
            {tradeIdea?.error && <span style={{ color: "#f87171" }}>{tradeIdea.error}</span>}
            {tradeIdea && !tradeIdea.error && (
              <>
                <div style={{ fontWeight: 800, fontSize: 13, color: "#4ade80", marginBottom: 6 }}>
                  💡 {tradeIdea.play} · {tradeIdea.ticker}
                </div>
                <div style={{ marginBottom: 8 }}>{tradeIdea.narrative}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 11 }}>
                  <KV k="Current" v={`$${tradeIdea.current_price?.toFixed(2)}`} />
                  <KV k="Target" v={`$${tradeIdea.target_price?.toFixed(2)}`} pos />
                  <KV k="Stop" v={`$${tradeIdea.stop_price?.toFixed(2)}`} neg />
                  <KV k="Contracts" v={tradeIdea.contracts} />
                  <KV k="Max gain" v={`$${tradeIdea.max_gain?.toFixed(2)}`} pos />
                  <KV k="Max loss" v={`$${tradeIdea.max_loss?.toFixed(2)}`} neg />
                  <KV k="Risk budget" v={`$${tradeIdea.risk_budget?.toFixed(0)}`} />
                  <KV k="Expected EV" v={`$${tradeIdea.expected_ev_per_contract?.toFixed(2)}`} pos={tradeIdea.expected_ev_per_contract > 0} />
                </div>
              </>
            )}
          </div>
        )}

        {/* Earnings scenarios */}
        {scenariosOpen && (
          <div data-testid={`news-card-scenario-body-${article.id}`} style={{
            marginTop: 4, padding: 12, borderRadius: 10,
            background: "rgba(0,0,0,0.3)", border: `1px solid ${accent}22`,
          }}>
            {busy && <div style={{ fontSize: 11, color: "var(--muted, #aaa)" }}>Running Monte-Carlo…</div>}
            {scenarios && !scenarios.error && (
              <>
                <div style={{ fontSize: 11, color: "var(--muted, #aaa)", marginBottom: 8, lineHeight: 1.5 }}>{scenarios.narrative}</div>
                <ScenarioBars scenarios={scenarios.scenarios} current={scenarios.current} accent={accent} />
              </>
            )}
            {scenarios?.error && <div style={{ fontSize: 11, color: "#f87171" }}>{scenarios.error}</div>}
          </div>
        )}
      </div>

      {/* iter 141 — Omega Summary popup. Opens on title tap; "Read full article" exits to source. */}
      {summaryOpen && (
        <OmegaSummaryModal
          article={article}
          accent={accent}
          sent={sent}
          loading={summaryLoading}
          data={summaryData}
          onClose={() => setSummaryOpen(false)}
        />
      )}
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// OmegaSummaryModal — popup card shown when the user taps a news headline.
// Shows Omega's brief take + deep-dive summary, and only then offers a "Read
// full article" button that opens the source (Yahoo / Finnhub / etc.) in a new
// tab. Theme-aware (uses CSS vars). No mock data — content comes from the real
// /api/market/news/deep-dive/{article.id} endpoint.
// ─────────────────────────────────────────────────────────────────────────────
function OmegaSummaryModal({ article, accent, sent, loading, data, onClose }) {
  const sourceHost = (() => {
    try { return new URL(article.link).hostname.replace(/^www\./, ""); }
    catch { return article.source || "source"; }
  })();
  // /api/market/news/deep-dive/{id} returns { breakdown: "...markdown..." }
  // — we render the markdown sections inline. Fall back to other shapes for
  // safety if the backend response evolves.
  const breakdown = data?.breakdown || data?.summary || data?.analysis || null;
  const omegaTake = article.omega_take;
  return (
    <div
      data-testid="news-summary-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "5vh 16px 16px",
        overflowY: "auto",
        animation: "newsModalFadeIn 180ms ease-out",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Omega article summary"
        style={{
          width: "100%", maxWidth: 640,
          background: "var(--surface, #0d1117)",
          color: "var(--text, #e6e6f5)",
          border: `1px solid ${accent}40`,
          borderRadius: 18,
          boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)",
          overflow: "hidden",
          animation: "newsModalSlideUp 220ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Header strip */}
        <div style={{
          padding: "14px 18px",
          background: `linear-gradient(120deg, ${accent}14, transparent)`,
          borderBottom: `1px solid var(--divider, rgba(255,255,255,0.06))`,
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "4px 10px", borderRadius: 999,
            background: `${accent}22`, color: accent,
            fontSize: 10, fontWeight: 800, letterSpacing: 1.4,
          }}>Ω OMEGA · SUMMARY</span>
          <span style={{
            padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 800,
            background: sent.bg, color: sent.text, border: `1px solid ${sent.border}`, letterSpacing: 0.6,
          }}>{sent.emoji} {(article.sentiment || "neutral").toUpperCase()}</span>
          {article.ticker && (
            <span style={{
              padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 800,
              background: "var(--layer2, rgba(255,255,255,0.04))", color: "var(--text)",
              border: `1px solid var(--border, rgba(255,255,255,0.08))`, letterSpacing: 0.6,
            }}>{article.ticker}</span>
          )}
          <span style={{ fontSize: 10, color: "var(--muted, #94a3b8)", marginLeft: "auto" }}>
            {article.publishedAgo || ""}
          </span>
          <button
            onClick={onClose}
            data-testid="news-summary-close"
            aria-label="Close summary"
            style={{
              background: "var(--layer2, rgba(255,255,255,0.04))",
              border: "1px solid var(--border, rgba(255,255,255,0.08))",
              color: "var(--text)",
              width: 28, height: 28, borderRadius: 8, padding: 0,
              fontSize: 16, lineHeight: "26px", cursor: "pointer", fontFamily: "inherit",
            }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ padding: "18px 20px 14px", maxHeight: "70vh", overflowY: "auto" }}>
          <h2 style={{
            fontSize: 18, fontWeight: 800, lineHeight: 1.3,
            color: "var(--text)", margin: 0, marginBottom: 8, letterSpacing: -0.2,
          }}>{article.title}</h2>
          <div style={{ fontSize: 11, color: "var(--muted, #94a3b8)", marginBottom: 14 }}>
            {article.source || sourceHost} · {article.publishedAgo || "recent"}
            {article.credibility && <> · {article.credibility} credibility</>}
            {article.impact_score != null && <> · impact {article.impact_score}/100</>}
          </div>

          {/* Brief take pulled from article (always available) */}
          {omegaTake && (
            <div style={{
              padding: 12, borderRadius: 12, marginBottom: 14,
              background: `${accent}10`, border: `1px solid ${accent}28`,
            }}>
              <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.4, color: accent, marginBottom: 6 }}>BRIEF TAKE</div>
              <div style={{ fontSize: 13, lineHeight: 1.55, color: "var(--text)" }}>{omegaTake}</div>
            </div>
          )}

          {/* Deep-dive Omega analysis from the real endpoint */}
          <div style={{ marginBottom: 4 }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.4, color: "var(--muted, #94a3b8)", marginBottom: 8 }}>
              OMEGA'S FULL ANALYSIS
            </div>
            {loading && (
              <div style={{ fontSize: 12, color: "var(--muted, #94a3b8)", padding: 14, textAlign: "center" }}>
                <span style={{ display: "inline-block", marginRight: 8, opacity: 0.8 }}>Ω</span>
                Omega is reading the article…
              </div>
            )}
            {!loading && data?.error && (
              <div style={{ fontSize: 12, color: "#f87171", padding: 10, borderRadius: 8, background: "rgba(248,113,113,0.06)" }}>
                {data.error}
              </div>
            )}
            {!loading && !data?.error && breakdown && (
              <BreakdownView text={breakdown} accent={accent} />
            )}
            {!loading && !data?.error && !breakdown && (
              <div style={{ fontSize: 12, color: "var(--muted)" }}>
                No deeper analysis available for this article. Read the source for the full details.
              </div>
            )}
          </div>
        </div>

        {/* Footer — only here does the user leave to Yahoo / source */}
        <div style={{
          padding: "12px 20px 16px",
          borderTop: `1px solid var(--divider, rgba(255,255,255,0.06))`,
          background: "var(--layer1, rgba(255,255,255,0.02))",
          display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
        }}>
          <button
            onClick={onClose}
            data-testid="news-summary-back"
            style={{
              padding: "9px 16px", borderRadius: 10,
              background: "var(--layer2, rgba(255,255,255,0.04))",
              border: "1px solid var(--border, rgba(255,255,255,0.08))",
              color: "var(--text)", fontSize: 12, fontWeight: 700,
              cursor: "pointer", fontFamily: "inherit",
            }}
          >‹ Back to feed</button>
          <a
            href={article.link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="news-summary-external"
            style={{
              marginLeft: "auto",
              padding: "9px 18px", borderRadius: 10,
              background: accent, color: "#fff",
              fontSize: 12, fontWeight: 800, letterSpacing: 0.3,
              textDecoration: "none", fontFamily: "inherit",
              display: "inline-flex", alignItems: "center", gap: 6,
              boxShadow: `0 4px 14px ${accent}55`,
            }}
          >Read full article on {sourceHost} →</a>
        </div>
      </div>
    </div>
  );
}

function ScenarioBars({ scenarios, current, accent }) {
  const rows = [
    { label: "Bull 90%", v: scenarios.bull_p90, color: "#4ade80" },
    { label: "Bull 70%", v: scenarios.bull_p70, color: "#86efac" },
    { label: "Base", v: scenarios.base, color: accent },
    { label: "Bear 70%", v: scenarios.bear_p70, color: "#fca5a5" },
    { label: "Bear 90%", v: scenarios.bear_p90, color: "#f87171" },
  ];
  const maxV = Math.max(...rows.map(r => r.v));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {rows.map(r => {
        const pct = (r.v / maxV) * 100;
        const delta = ((r.v - current) / current) * 100;
        return (
          <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
            <span style={{ width: 60, color: "var(--muted, #aaa)", fontWeight: 700 }}>{r.label}</span>
            <div style={{ flex: 1, height: 18, background: "rgba(255,255,255,0.04)", borderRadius: 4, position: "relative", overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: r.color, opacity: 0.4 }} />
            </div>
            <span style={{ width: 64, textAlign: "right", color: r.color, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>${r.v.toFixed(2)}</span>
            <span style={{ width: 50, textAlign: "right", color: "var(--muted, #aaa)", fontSize: 10, fontVariantNumeric: "tabular-nums" }}>
              {delta > 0 ? "+" : ""}{delta.toFixed(1)}%
            </span>
          </div>
        );
      })}
    </div>
  );
}


function KV({ k, v, pos, neg }) {
  const color = pos ? "#4ade80" : neg ? "#f87171" : "var(--text, #e6e6f5)";
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: "1px dashed rgba(255,255,255,0.06)" }}>
      <span style={{ color: "var(--muted, #aaa)" }}>{k}</span>
      <span style={{ color, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{v}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BreakdownView — lightweight renderer for the markdown-ish "### Section" output
// from /api/market/news/deep-dive. Splits on `### Header` and renders each
// section as a header + body card. No external markdown dep.
// ─────────────────────────────────────────────────────────────────────────────
function BreakdownView({ text, accent }) {
  if (!text) return null;
  // Normalize: prefix with newline so the regex splits on the very first
  // `### Header` too. Then split on any `### ` preceded by a newline.
  const normalized = "\n" + String(text).trim();
  const parts = normalized.split(/\n\s*###\s+/).map(s => s.trim()).filter(Boolean);
  // If there's no `###` at all, render as one section
  const sections = parts.length
    ? parts.map(chunk => {
        const lines = chunk.split(/\n/);
        const header = (lines.shift() || "").trim();
        return { header, body: lines.join("\n").trim() };
      })
    : [{ header: null, body: text.trim() }];
  return (
    <div style={{ display: "grid", gap: 10 }}>
      {sections.map((s, i) => (
        <div key={i} style={{
          padding: 12, borderRadius: 10,
          background: "var(--layer1, rgba(255,255,255,0.025))",
          border: "1px solid var(--border, rgba(255,255,255,0.06))",
        }}>
          {s.header && (
            <div style={{
              fontSize: 10, fontWeight: 800, letterSpacing: 1.2,
              color: accent, marginBottom: 6, textTransform: "uppercase",
            }}>{s.header}</div>
          )}
          <div style={{
            fontSize: 13, lineHeight: 1.6, color: "var(--text)",
            whiteSpace: "pre-wrap",
          }}>{s.body}</div>
        </div>
      ))}
    </div>
  );
}
